import { useState, useEffect } from 'react';
import { staffAPI, guildsAPI } from '../../api';
import StaffPicker from '../../components/ui/StaffPicker';
import api from '../../api';

export default function BonusesPage({ guild }) {
  const [staff, setStaff] = useState([]);
  const [ranks, setRanks] = useState([]);
  const [bonuses, setBonuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('grant');
  const [submitting, setSubmitting] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState([]);
  const sym = guild?.currency_symbol || '$';

  // Grant bonus form state
  const [bonusForm, setBonusForm] = useState({
    type: 'manual', // manual, bulk, holiday
    target: 'all', // all, rank, individuals
    amount: '',
    reason: '',
    rankTarget: '',
    individuals: '',
    holidayName: '',
    scheduleDate: '',
    bonusType: 'flat',
  });

  const loadData = async () => {
    try {
      const [staffRes, ranksRes, bonusRes] = await Promise.all([
        staffAPI.getAll(guild.id),
        guildsAPI.getRanks(guild.id),
        api.get(`/api/bonuses/${guild.id}`),
      ]);
      setStaff(staffRes.data);
      setRanks(ranksRes.data);
      setBonuses(bonusRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, [guild.id]);

  const handleSubmit = async (overrideType) => {
    const submitType = overrideType || bonusForm.type;
    if (!bonusForm.amount || parseFloat(bonusForm.amount) <= 0) return alert('Please enter a valid amount.');
    setSubmitting(true);
    try {
      await api.post(`/api/bonuses/${guild.id}`, {
        type: submitType,
        target: bonusForm.target,
        amount: parseFloat(bonusForm.amount),
        reason: bonusForm.reason || 'Dashboard bonus',
        rank_name: bonusForm.rankTarget,
        individuals: bonusForm.individuals,
        holiday_name: bonusForm.holidayName,
        schedule_date: bonusForm.scheduleDate || null,
        bonus_type: bonusForm.bonusType,
      });
      alert('Bonus granted successfully!');
      await loadData();
      setBonusForm({ type: 'manual', target: 'all', amount: '', reason: '', rankTarget: '', individuals: '', holidayName: '', scheduleDate: '', bonusType: 'flat' });
      setSelectedStaff([]);
    } catch (err) {
      alert('Failed to grant bonus. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return (
    <div style={{ textAlign: 'center', padding: '60px', color: 'var(--text-muted)' }}>
      <div style={{ fontSize: '40px', marginBottom: '12px' }}>⏳</div>Loading bonuses...
    </div>
  );

  const inputStyle = {
    width: '100%', padding: '10px 12px', boxSizing: 'border-box',
    border: '2px solid var(--border)', borderRadius: '10px',
    fontSize: '14px', fontFamily: 'var(--font-body)',
    background: 'var(--bg-card)', color: 'var(--text-primary)', outline: 'none',
  };

  const labelStyle = {
    fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)',
    display: 'block', marginBottom: '6px',
  };

  return (
    <div>
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ fontFamily: 'var(--font-display)', fontSize: '32px', color: 'var(--text-primary)', marginBottom: '6px' }}>Bonuses</h1>
        <p style={{ color: 'var(--text-secondary)', fontSize: '15px' }}>Grant and manage staff bonuses</p>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '4px', marginBottom: '24px', background: 'var(--bg-secondary)', borderRadius: '12px', padding: '4px', width: 'fit-content' }}>
        {[
          { key: 'grant', label: '🎁 Grant Bonus' },
          { key: 'history', label: `📋 History (${bonuses.length})` },
        ].map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} style={{
            padding: '8px 18px', borderRadius: '8px', cursor: 'pointer',
            border: 'none', fontFamily: 'var(--font-body)', fontSize: '13px', fontWeight: 600,
            background: tab === t.key ? 'var(--bg-card)' : 'transparent',
            color: tab === t.key ? 'var(--text-primary)' : 'var(--text-muted)',
            boxShadow: tab === t.key ? 'var(--shadow-card)' : 'none',
          }}>{t.label}</button>
        ))}
      </div>

      {tab === 'grant' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
          {/* Bulk Bonus */}
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '16px', padding: '24px', boxShadow: 'var(--shadow-card)' }}>
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '20px', color: 'var(--text-primary)', marginBottom: '6px' }}>
              👥 Bulk Bonus
            </h2>
            <p style={{ fontSize: '13px', color: 'var(--text-muted)', marginBottom: '20px' }}>
              Grant a bonus to multiple staff at once
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div>
                <label style={labelStyle}>Who receives the bonus</label>
                <select value={bonusForm.target} onChange={e => setBonusForm(p => ({ ...p, target: e.target.value }))} style={inputStyle}>
                  <option value="all">All active staff</option>
                  <option value="rank">By rank</option>
                  <option value="active_period">Active this period</option>
                  <option value="individuals">Select individuals</option>
                </select>
              </div>
              {bonusForm.target === 'rank' && (
                <div>
                  <label style={labelStyle}>Rank</label>
                  <select value={bonusForm.rankTarget} onChange={e => setBonusForm(p => ({ ...p, rankTarget: e.target.value }))} style={inputStyle}>
                    <option value="">Select a rank...</option>
                    {ranks.map(r => <option key={r.id} value={r.name}>{r.name}</option>)}
                  </select>
                </div>
              )}
              {bonusForm.target === 'individuals' && (
                <div>
                  <label style={labelStyle}>Select Staff Members</label>
                  <StaffPicker
                    staff={staff}
                    selected={selectedStaff}
                    onChange={(s) => {
                      setSelectedStaff(s);
                      setBonusForm(p => ({ ...p, individuals: s.map(m => m.discord_id).join(',') }));
                    }}
                    placeholder="Search by name..."
                  />
                  {selectedStaff.length > 0 && (
                    <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '4px' }}>
                      {selectedStaff.length} staff member{selectedStaff.length !== 1 ? 's' : ''} selected
                    </div>
                  )}
                </div>
              )}
              <div>
                <label style={labelStyle}>Amount ({sym})</label>
                <input type="number" placeholder="50.00" value={bonusForm.amount}
                  onChange={e => setBonusForm(p => ({ ...p, amount: e.target.value }))} style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>Reason (optional)</label>
                <input type="text" placeholder="Great work this week!" value={bonusForm.reason}
                  onChange={e => setBonusForm(p => ({ ...p, reason: e.target.value }))} style={inputStyle} />
              </div>
              <button onClick={() => handleSubmit('bulk')}
                disabled={submitting} style={{
                  padding: '12px', background: 'var(--gradient-accent)', color: '#fff',
                  border: 'none', borderRadius: '10px', fontSize: '14px',
                  fontWeight: 700, cursor: submitting ? 'not-allowed' : 'pointer',
                  fontFamily: 'var(--font-display)',
                }}>
                {submitting ? 'Processing...' : '💸 Grant Bulk Bonus'}
              </button>
            </div>
          </div>

          {/* Holiday Bonus */}
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '16px', padding: '24px', boxShadow: 'var(--shadow-card)' }}>
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '20px', color: 'var(--text-primary)', marginBottom: '6px' }}>
              🎊 Holiday Bonus
            </h2>
            <p style={{ fontSize: '13px', color: 'var(--text-muted)', marginBottom: '20px' }}>
              Named bonus event — immediate or scheduled
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div>
                <label style={labelStyle}>Event Name</label>
                <input type="text" placeholder="Christmas Bonus, Anniversary..." value={bonusForm.holidayName}
                  onChange={e => setBonusForm(p => ({ ...p, holidayName: e.target.value }))} style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>Bonus Type</label>
                <select value={bonusForm.bonusType} onChange={e => setBonusForm(p => ({ ...p, bonusType: e.target.value }))} style={inputStyle}>
                  <option value="flat">Flat amount ({sym})</option>
                  <option value="percent">Percentage of earnings (%)</option>
                </select>
              </div>
              <div>
                <label style={labelStyle}>Amount ({bonusForm.bonusType === 'percent' ? '%' : sym})</label>
                <input type="number" placeholder={bonusForm.bonusType === 'percent' ? '10' : '100.00'} value={bonusForm.amount}
                  onChange={e => setBonusForm(p => ({ ...p, amount: e.target.value }))} style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>Schedule Date (optional — leave blank to pay now)</label>
                <input type="date" value={bonusForm.scheduleDate}
                  onChange={e => setBonusForm(p => ({ ...p, scheduleDate: e.target.value }))} style={inputStyle} />
              </div>
              <button onClick={() => handleSubmit('holiday')}
                disabled={submitting} style={{
                  padding: '12px', background: 'var(--gradient-accent)', color: '#fff',
                  border: 'none', borderRadius: '10px', fontSize: '14px',
                  fontWeight: 700, cursor: submitting ? 'not-allowed' : 'pointer',
                  fontFamily: 'var(--font-display)',
                }}>
                {submitting ? 'Processing...' : bonusForm.scheduleDate ? '📅 Schedule Bonus' : '🎊 Grant Holiday Bonus'}
              </button>
            </div>
          </div>
        </div>
      )}

      {tab === 'history' && (
        <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '16px', overflow: 'hidden', boxShadow: 'var(--shadow-card)' }}>
          {bonuses.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px', color: 'var(--text-muted)' }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>🎁</div>
              No bonus history yet.
            </div>
          ) : bonuses.map(b => (
            <div key={b.id} style={{
              padding: '16px 20px', borderBottom: '1px solid var(--border)',
              display: 'flex', alignItems: 'center', gap: '16px',
            }}>
              <div style={{
                width: '40px', height: '40px', borderRadius: '10px',
                background: 'rgba(245,166,35,0.15)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '20px', flexShrink: 0,
              }}>🎁</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text-primary)' }}>
                  {b.display_name || b.username || 'Staff Member'}
                  <span style={{ marginLeft: '8px', fontSize: '12px', fontWeight: 400, color: 'var(--text-muted)', background: 'var(--bg-secondary)', padding: '2px 8px', borderRadius: '100px' }}>
                    {b.type}
                  </span>
                </div>
                <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginTop: '2px' }}>
                  {b.reason || 'No reason provided'}
                </div>
                <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>
                  {new Date(b.granted_at).toLocaleString()}
                </div>
              </div>
              <div style={{ fontSize: '16px', fontWeight: 700, color: 'var(--accent-secondary)', flexShrink: 0 }}>
                +{sym}{parseFloat(b.amount).toFixed(2)}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
